# Route: /tracking/records/new
# Create a new tracking record from a selected template
# Permission: TrackingRecord:create

page-tracking-record-create:
  type: Page
  archetype: create
  meta:
    title: $t.tracking.create.meta_title
    route: /tracking/records/new
    layout: shell
    auth: required
  data:
    form:
      model: CreateRecordForm
      initial:
        text: ""
        # F53b §B — voice input flips this to "Voice" when the operator
        # commits a transcript via the mic button below. Keyboard remains
        # the default for plain typing.
        inputSource: Keyboard
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - create-header
    - create-form
    - create-attachments
    - create-review-path

# ── Header ───────────────────────────────────────────────────────────────────

# F80 FR-3 scopes the context chip row to the list, detail and sign screens.
# Create is deliberately excluded: the asset and tracking pickers below ARE the
# context statement, and they are editable — a chip row restating the selection
# a few pixels above the control that owns it puts the same fact on screen three
# times (chips, combos, page title) and gives the operator two places to read a
# value only one of them can change.
create-header:
  type: PageHeader
  content: $t.tracking.create.title
  back_route: /tracking

# ── Entry form ───────────────────────────────────────────────────────────────
#
# The Form has no card tokens. Fields are wrapped in an inner Section card so
# that sticky FormActions (on sm/md) sits outside the card border and does not
# visually overlap it.

create-form:
  type: Form
  model: CreateRecordForm
  a11y:
    label: $t.tracking.create.title
  action:
    type: create
    model: TrackingRecord
    on_success: navigate(/tracking/records)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - create-fields-card
    - create-form-actions

create-fields-card:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - create-dynamic-form
    - create-narrative-wrapper
    - create-reason
    - create-field-input-source

# F80 FR-2 / M1 (tracking-record-list-context) FR-1 — the widget owns the
# Asset + General create flow: for Asset, an eligible-asset picker
# (GET /api/tracking/subjects) then a dependent declared-tracking picker
# (GET /api/tracking/assignments/by-asset/:id); for General, no asset picker — the template
# list comes directly from GET /api/tracking/forms/by-context/General.
# Both paths render the same dynamic per-field form. Area is intentionally
# absent for M1: it needs a non-null contextId, sourced from the existing
# Location aggregate (see the backlog area/location-context item). Deep links:
# ?asset=<id>[&template=<id>] preselect and force the Asset path.
create-dynamic-form:
  type: Widget
  ref: xp87-tracking-record-form

# The narrative and its dictation, stacked: the textarea first, the microphone row
# under it. Nothing in the row is positioned over the field, so the text stays
# fully visible while it is written.
create-narrative-wrapper:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.xs
  children:
    - create-field-text
    - create-voice-mic

create-field-text:
  type: Input
  input_type: textarea
  label: $t.tracking.create.field_narrative
  bind: form.text
  required: true
  a11y:
    label: $t.tracking.create.field_narrative_label

# Voice dictation toggle — a microphone icon button in a row below the narrative
# textarea, with a short hint beside it; the full instruction is the button's
# title and accessible description. The microphone is off until pressed. It
# records through the one voice module (xp87-voice.js): each segment's raw text
# is appended to the textarea (id "create-field-text") while recording, the
# textarea wears a caution-coloured ring and the row reads "Listening…";
# clicking again stops, and the corrected text replaces it with the changed
# words shown marked in the row. A committed
# transcript flips the hidden inputSource field (id "create-field-input-source")
# to "Voice". A recording stops by itself at 10 minutes and says so. The mic
# renders only when the deployment offers transcription; each failure kind shows
# its own localized live feedback — never silent. Recognition
# runs on the server, in the caller's resolved locale.
create-voice-mic:
  type: Widget
  ref: xp87-voice-mic-button
  params:
    target-id: create-field-text
    source-field-id: create-field-input-source
    hint-message: $t.tracking.create.voice_tip
    description-message: $t.tracking.create.voice_tip_detail
    label-start: $t.tracking.create.voice_btn_start
    label-stop: $t.tracking.create.voice_btn_stop
    label-listening: $t.tracking.create.voice_status_listening
    label-correcting: $t.tracking.create.voice_status_correcting
    label-corrected: $t.tracking.create.voice_corrected
    label-limit: $t.tracking.create.voice_limit
    label-error-permission: $t.tracking.create.voice_err_permission
    label-error-audio: $t.tracking.create.voice_err_audio_capture
    label-error-recording: $t.tracking.create.voice_err_recording
    label-error-network: $t.tracking.create.voice_err_network
    label-error-provider: $t.tracking.create.voice_err_provider
    label-error-unavailable: $t.tracking.create.voice_err_unavailable
    label-error-rate-limited: $t.tracking.create.voice_err_rate_limited
    label-error-too-large: $t.tracking.create.voice_err_too_large
    label-error-unsupported: $t.tracking.create.voice_err_unsupported
    label-error-no-speech: $t.tracking.create.voice_err_no_speech
    label-error-generic: $t.tracking.create.voice_err_generic

# The reason, after the fields and the narrative it justifies. The ONE reason
# control: the policy comes from the server, for the tracking form the operator
# picked above — which is only known at run time, so the field takes its subject
# from the record form's xp87-record-form-changed event rather than a route param.
create-reason:
  type: Widget
  ref: xp87-reason-field
  params:
    transaction: TrackingRecord.create
    subject-event: xp87-record-form-changed
    subject-key: formId
    action-model: TrackingRecord
    action-type: create
    node-prefix: record-create-reason

# Hidden field — the API requires InputSource on create. Defaults to
# "Keyboard"; the voice mic toggle above flips it to "Voice" when a dictated
# transcript is committed. Rendered with no label/wrapper so it adds no
# vertical space.
create-field-input-source:
  type: Input
  input_type: hidden
  bind: form.inputSource

create-form-actions:
  type: FormActions
  submit_label: $t.tracking.create.btn_submit
  cancel_route: /tracking

# Informational only: sits last (after the fields, the submit action, and the
# attachments), so it cannot be read as an input and mirrors the record-detail
# order (content → attachments → signatures). The record-form widget emits the
# selected template's signature slots on the existing xp87-record-form-changed
# event; this component is their single read-only renderer. A template without
# slots is explicit N/A.
create-review-path:
  type: Widget
  ref: xp87-tracking-review-path
  params:
    title: $t.tracking.create.review_path_title
    label-na: $t.tracking.create.review_path_na
    label-loading: $t.tracking.create.review_path_loading
    label-index: $t.tracking.create.review_path_index
    label-role: $t.tracking.create.sig_slot_role

# ── Attach-on-create ─────────────────────────────────────────────────────────
# The same attachment surface as the entry detail (upload + file list +
# click-to-preview), available while creating. A tracking record must exist to
# hold a file, so the FIRST upload lazily materialises the Draft entry via the
# record-form (xp87-record-ensure-created ↔ xp87-record-created), then attaches;
# later uploads attach directly. The narrative is immutable once the entry is
# created (no update path — only sign/amend/attach), so the form locks on the
# first create and this card shows a "Finish" back to the entry list. The plain
# "create then navigate to the list" path (no attachment) is unchanged: the
# Form's submit still owns its submissionId-gated navigation.
create-attachments:
  type: Widget
  ref: xp87-record-attachments
  params:
    mode: create
